import { Component } from '@mycolorway/tao';
import { MDCList } from '@material/list';
import eventListenerMixin from 'next/javascripts/mixins/event-listener-mixin';
import { closest } from 'next/javascripts/modules/dom';

Component('tr-select-list', {
  properties: {
    emptyMessage: {
      type: String,
      default: '没有找到匹配的选项',
    },

    createable: {
      type: Boolean,
      default: false,
    },

    creationMessage: {
      type: String,
      default: '创建新的选项：',
    },

    filterValue: {
      type: String,
      default: '',
    },
  },

  mixins: [eventListenerMixin],

  init() {
    this.options = [];
    this.selected = [];
  },

  connected() {
    this.listenTo(this, 'click', this.handleListItemClick.bind(this));
  },

  disconnected() {
    this.stopListening();
    if (this.mdcList) this.mdcList.destroy();
  },

  setOptions(options) {
    this.highlightedItem = null;
    this.options = options;
    this.render();
  },

  selectOption(option) {
    if (this.selected.findIndex(s => s.value === option.value) === -1) {
      this.selected.push(option);

      const item = this.querySelector(`.mdc-list .mdc-list-item[data-value="${option.value}"]`);
      if (item) item.classList.add('mdc-list-item--selected');
    }
  },

  unselectOption(option) {
    const selectedIndex = this.selected.findIndex(s => s.value === option.value);

    if (selectedIndex !== -1) {
      this.selected.splice(selectedIndex, 1);

      const item = this.querySelector(`.mdc-list .mdc-list-item[data-value="${option.value}"]`);
      if (item) item.classList.remove('mdc-list-item--selected');
    }
  },

  highlightPrevItem() {
    if (this.highlightedItem) {
      const prevItem = this.getPrevListItem(this.highlightedItem);
      if (prevItem) this.highlightItem(prevItem);
    } else {
      this.highlightFirstItem();
    }
  },

  highlightNextItem() {
    if (this.highlightedItem) {
      const nextItem = this.getNextListItem(this.highlightedItem);
      if (nextItem) this.highlightItem(nextItem);
    } else {
      this.highlightFirstItem();
    }
  },

  highlightFirstItem() {
    this.highlightItem(this.querySelector('.mdc-list-item'));
  },

  highlightItem(item) {
    if (!item || item.classList.contains('highlighted')) return;

    Array.prototype.forEach.call(
      this.querySelectorAll('.mdc-list-item.highlighted'),
      listItem => listItem.classList.remove('highlighted'),
    );

    item.classList.add('highlighted');
    this.highlightedItem = item;
    this.scrollToItem(this.highlightedItem);
  },

  getPrevListItem(item) {
    const items = this.getListItems();
    const itemIndex = items.findIndex(el => el.isSameNode(item));
    if (itemIndex !== -1 && itemIndex !== 0) return items[itemIndex - 1];

    return null;
  },

  getNextListItem(item) {
    const items = this.getListItems();
    const itemIndex = items.findIndex(el => el.isSameNode(item));
    if (itemIndex !== -1 && itemIndex !== items.length - 1) return items[itemIndex + 1];

    return null;
  },

  getListItems() {
    return Array.prototype.slice.call(
      this.querySelectorAll('.mdc-list-item:not(.mdc-list-item--disabled)'),
    );
  },

  clearSelected() {
    this.selected = [];
    Array.prototype.forEach.call(
      this.querySelectorAll('.mdc-list-item--selected'),
      item => item.classList.remove('mdc-list-item--selected'),
    );
  },

  render() {
    if (this.mdcList) this.mdcList.destroy();

    const hasGroup = this.options.some(option => option.data.group);
    const list = hasGroup ? this.renderGroupedList() : this.renderList();

    if (this.createable && this.filterValue) {
      const value = this.filterValue.toLowerCase();
      if (!this.options.some(option => option.text.toLowerCase() === value)) {
        list.insertAdjacentHTML(
          'beforeend',
          `<li class="mdc-list-item" data-value="creation-message" tabindex="-1">${this.creationMessage}${this.filterValue}</li>`,
        );
        this.filterValue = '';
      }
    }

    if (this.options.length > 0 || this.createable) {
      this.innerHTML = list.outerHTML;
      this.initMDCList();
    } else {
      this.renderEmptyMessage();
    }
  },

  renderGroupedList() {
    const wrapper = document.createElement('div');
    wrapper.classList.add('mdc-list-group');

    const groupedOptions = {};
    const unGroupedOptions = [];
    this.options.forEach((option) => {
      if (!option.data.group) {
        unGroupedOptions.push(option);
      } else if (groupedOptions[option.data.group]) {
        groupedOptions[option.data.group].push(option);
      } else {
        groupedOptions[option.data.group] = [option];
      }
    });

    if (unGroupedOptions.length > 0) wrapper.appendChild(this.renderList(unGroupedOptions));

    Object.keys(groupedOptions).forEach((groupName) => {
      if (groupName) {
        wrapper.appendChild(this.renderGroup(groupName));
      }
      wrapper.appendChild(this.renderList(groupedOptions[groupName]));
    });

    return wrapper;
  },

  renderGroup(name) {
    const group = document.createElement('div');
    group.classList.add('mdc-list-group__subheader');
    group.textContent = name;
    return group;
  },

  renderList(options = this.options) {
    const list = document.createElement('ul');
    const optionsFragement = document.createDocumentFragment();
    list.classList.add('mdc-list');

    if (options.some(option => option.data.avatar)) {
      list.classList.add('mdc-list--avatar-list');
    }

    if (options.some(option => option.data.desc)) {
      list.classList.add('mdc-list--two-line');
    }

    options.forEach(option => optionsFragement.appendChild(this.renderOption(option)));
    list.appendChild(optionsFragement);

    return list;
  },

  renderOption(option) {
    const item = document.createElement('li');

    item.classList.add('mdc-list-item');
    if (this.selected.findIndex(s => s.value === option.value) !== -1) {
      item.classList.add('mdc-list-item--selected');
    }

    item.setAttribute('tabindex', -1);
    item.setAttribute('data-value', option.value);

    Object.keys(option.dataAttributes)
      .forEach(key => item.setAttribute(`data-${key}`, option.dataAttributes[key]));

    const itemGraphic = this.renderOptionGraphic(option);
    if (itemGraphic) item.insertAdjacentHTML('beforeend', itemGraphic);

    const itemText = this.renderOptionText(option);
    item.insertAdjacentElement('beforeend', itemText);

    item.insertAdjacentHTML('beforeend', `
      <span class="mdc-list-item__meta selected-icon">
        <i class="twr twr-check"></i>
      </span>
    `);

    return item;
  },

  renderOptionGraphic(option) {
    if (option.data.avatar) {
      return `
        <span class="mdc-list-item__graphic avatar">
          <img src="${option.data.avatar}" alt="${option.text}" />
        </span>
      `;
    }

    if (option.data.icon) {
      return `
        <span class="mdc-list-item__graphic icon">
          <i class="twr twr-${option.data.icon}"></i>
        </span>
      `;
    }

    return null;
  },

  renderOptionText(option) {
    const itemText = document.createElement('span');
    itemText.classList.add('mdc-list-item__text');

    if (option.data.desc) {
      itemText.insertAdjacentHTML('beforeend', `
        <span class="mdc-list-item__primary-text">${option.text}</span>
        <span class="mdc-list-item__secondary-text">${option.data.desc}</span>
      `);
    } else {
      itemText.textContent = option.text;
    }

    return itemText;
  },

  renderEmptyMessage() {
    this.innerHTML = `<div class="empty-message">${this.emptyMessage}</div>`;
  },

  initMDCList() {
    this.mdcList = new MDCList(this.querySelector('.mdc-list'));
    this.mdcList.singleSelection = false;
  },

  handleListItemClick(event) {
    const item = closest(event.target, '.mdc-list-item');
    if (!item) return;
    if (item.dataset.value === 'creation-message') {
      this.namespacedTrigger('create');
    } else {
      this.namespacedTrigger('select', { detail: item });
    }
  },

  scrollToItem(item) {
    if (item.scrollIntoViewIfNeeded) {
      item.scrollIntoViewIfNeeded();
      return;
    }

    const top = item.offsetTop;
    const height = item.clientHeight;
    const parent = item.offsetParent;
    const parentHeight = parent.clientHeight;

    const overTop = top < parent.scrollTop;
    const overBottom = (top + height) > (parent.scrollTop + parentHeight);

    if (overTop || overBottom) {
      parent.scrollTop = top - parentHeight / 2 + height / 2;
    }
  },

  scrollToSelectedItem() {
    const selectedItem = this.querySelector('.mdc-list-item--selected');
    if (selectedItem) this.scrollToItem(selectedItem);
  },
});
